'use client';

import { ENVIRONMENT_MAPPING } from '@/config/constants';
import { datadogRum } from '@datadog/browser-rum';
import { createContext, useEffect } from 'react';

import pkg from '../../package.json';

type DatadogContextValue = typeof datadogRum;

const DatadogContext = createContext<DatadogContextValue>(datadogRum);

export const DatadogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  useEffect(() => {
    const isDisabled = localStorage.getItem('disableDatadog');

    // Don't initialize Datadog in development
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'development' || isDisabled) {
      return;
    }

    if (
      !process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID ||
      !process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ||
      !process.env.NEXT_PUBLIC_DATADOG_SITE
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        'Datadog not configured, check environment variables: NEXT_PUBLIC_DATADOG_APPLICATION_ID, NEXT_PUBLIC_DATADOG_CLIENT_TOKEN, NEXT_PUBLIC_DATADOG_SITE',
      );
      return;
    }

    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      site: process.env.NEXT_PUBLIC_DATADOG_SITE,
      env: ENVIRONMENT_MAPPING[
        process.env.NEXT_PUBLIC_VERCEL_ENV as keyof typeof ENVIRONMENT_MAPPING
      ],
      service: 'offer-tool-international',
      version: pkg.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [
        'https://angebot.staging.1komma5grad.com/',
        'https://angebot.1komma5grad.com/',
      ],
      enableExperimentalFeatures: ['feature_flags'],
    });

    datadogRum.startSessionReplayRecording();
  }, []);

  return (
    <DatadogContext.Provider value={datadogRum}>
      {children}
    </DatadogContext.Provider>
  );
};
